"use client";

import { Moon, Sun } from "lucide-react";
import { motion } from "motion/react";
import { useTheme } from "next-themes";
import { useCallback, useEffect, useState } from "react";
import { cn } from "@/lib/utils";

const themes = [
    {
        key: "light",
        icon: Sun,
        label: "Light theme",
    },
    {
        key: "dark",
        icon: Moon,
        label: "Dark theme",
    },
] as const;

export type ThemeType = "light" | "dark";

export type ThemeSwitcherProps = {
    value?: ThemeType;
    onChange?: (theme: ThemeType) => void;
    defaultValue?: ThemeType;
    className?: string;
    layoutId?: string;
};

export const ThemeSwitcher = ({
    value,
    onChange,
    defaultValue = "dark",
    className,
    layoutId = "activeThemeSwitcher",
}: ThemeSwitcherProps) => {
    const { theme: nextTheme, setTheme: setNextTheme } = useTheme();
    const [mounted, setMounted] = useState(false);
    const [uncontrolledTheme, setUncontrolledTheme] = useState<ThemeType>(defaultValue);

    // Prevent hydration mismatch
    useEffect(() => {
        setMounted(true);
    }, []);

    const activeTheme =
        value !== undefined
            ? value
            : mounted && nextTheme
            ? (nextTheme as ThemeType)
            : uncontrolledTheme;

    const handleThemeClick = useCallback(
        (themeKey: ThemeType) => {
            if (onChange) {
                onChange(themeKey);
            } else {
                setNextTheme(themeKey);
            }
            setUncontrolledTheme(themeKey);
        },
        [onChange, setNextTheme]
    );

    if (!mounted) {
        return (
            <div
                className={cn(
                    "relative isolate flex h-8 items-center rounded-full bg-background p-1 ring-1 ring-border shadow-2xs opacity-0",
                    className
                )}
                aria-hidden="true"
            >
                <div className="flex size-6 items-center justify-center" />
                <div className="flex size-6 items-center justify-center" />
            </div>
        );
    }

    return (
        <div
            className={cn(
                "relative isolate flex h-8 items-center rounded-full bg-background p-1 ring-1 ring-border shadow-2xs",
                className
            )}
        >
            {themes.map(({ key, icon: Icon, label }) => {
                const isActive = activeTheme === key;

                return (
                    <button
                        aria-label={label}
                        className={cn(
                            "relative flex size-6 items-center justify-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer",
                            isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                        )}
                        key={key}
                        onClick={() => handleThemeClick(key)}
                        type="button"
                    >
                        {isActive && (
                            <motion.div
                                className="absolute inset-0 rounded-full bg-secondary shadow-xs"
                                layoutId={layoutId}
                                transition={{ type: "spring", stiffness: 400, damping: 30 }}
                            />
                        )}
                        <Icon
                            className={cn(
                                "relative z-10 size-3.5 transition-colors",
                                isActive ? "text-foreground" : "text-muted-foreground"
                            )}
                        />
                    </button>
                );
            })}
        </div>
    );
};